import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { resolveSupportProfileFacts } from '../knowledgeItems/supportProfileResolver';
import { CARE_LENS, getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitlyPlacedInAnotherChapter,
} from './profilePlacementMetadata';
import { resolveKnowledgeFactsForProfileChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export const WHAT_HELPS_DOMAIN_ID = 'DM_03';

const GROUPS = Object.freeze({
  comfort_calm: 'Comfort and calm',
  helpful_approaches: 'Helpful approaches',
  familiar_routines_surroundings: 'Familiar routines and surroundings',
  participation_success: 'Participation and success',
  difficult_moments: 'During difficult moments',
});

const DEFAULT_GROUP_ORDER = Object.freeze(Object.keys(GROUPS));
const ELIGIBLE_PROFILE_FIELDS = Object.freeze([
  'whatHelps',
  'thingsThatUsuallyHelp',
  'familySupportTips',
]);

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const valueKey = (value = '') => cleanText(value).toLowerCase();
const getFactValue = (fact = {}) => cleanText(fact.summary || fact.title);

const buildProvenance = (facts = []) => {
  const sourceRefs = facts.flatMap((fact) => fact.sourceRefs || []);
  return normalizeChapterProvenance(sourceRefs, 'what-helps-source');
};

// DM_03: attach sourceAction only for real Knowledge facts. Synthetic legacy
// facts (from supportProfile.whatHelps/thingsThatUsuallyHelp/familySupportTips)
// have no Knowledge doc behind them, so they never get one - there is nothing
// a Knowledge write path could update, and they must not route to old Profile
// editors. Classification-by-sourceType itself is shared; see
// knowledgeSourceAction.js.
const resolveFactSourceAction = (fact) => {
  if (fact.synthetic) return undefined;
  return resolveKnowledgeSourceAction(fact);
};

const classifyFact = (value = '') => {
  const text = cleanText(value).toLowerCase();
  if (/\b(?:comfort|calm|settle|quiet|blanket|music|sensory|rest|reassur|sooth)\b/.test(text)) {
    return 'comfort_calm';
  }
  if (/\b(?:during distress|distressed|overwhelm|escalat|meltdown|shutdown|hard moment|stay nearby|reduce language)\b/.test(text)) {
    return 'difficult_moments';
  }
  if (/\b(?:familiar|routine|predictable|same order|surroundings|known person)\b/.test(text)) {
    return 'familiar_routines_surroundings';
  }
  if (/\b(?:choose|choice|participat|success|activit|task|independent|engag)\b/.test(text)) {
    return 'participation_success';
  }
  return 'helpful_approaches';
};

const buildLongDirectEntryHeadline = (value = '') => {
  const text = cleanText(value).toLowerCase();
  const concepts = [
    {
      matches: /\b(?:heads? up|advance warning|warning before)\b.*\b(?:transition|change activit)/,
      label: 'Heads-up before transitions',
    },
    {
      matches: /\b(?:quiet|calm)\b.*\b(?:time|space|place|minutes?|sit)\b|\b(?:sit|time)\b.*\bquiet\b/,
      label: 'quiet time',
    },
    {
      matches: /\bheadphones?\b/,
      label: 'headphones',
    },
    {
      matches: /\b(?:fewer words|short(?:er)? (?:words|phrases|sentences)|reduce language)\b/,
      label: 'fewer words when upset',
    },
  ];
  const labels = concepts.filter((concept) => concept.matches.test(text)).map((concept) => concept.label);
  if (labels.length) {
    return labels.slice(0, 2).join(' and ').replace(/^./, (letter) => letter.toUpperCase());
  }
  if (/\b(?:distress|upset|escalat|overwhelm|meltdown|shutdown)\b/.test(text)) {
    return 'Support during difficult moments';
  }
  if (/\b(?:routine|predictable|familiar)\b/.test(text)) {
    return 'Familiar routines and preparation';
  }
  return 'Helpful approach from your original note';
};

const buildHandbookCopy = (value = '', { isDirectCaregiverEntry = false } = {}) => {
  const text = cleanText(value).replace(/[.]+$/, '');
  if (isDirectCaregiverEntry && text.length > 140) {
    return {
      headline: buildLongDirectEntryHeadline(text),
      explanation: `${text}.`,
    };
  }
  const helpsMatch = text.match(/^(?:a |an |the )?(.+?) helps? (.+)$/i);
  if (helpsMatch) {
    return {
      headline: helpsMatch[1].replace(/^./, (letter) => letter.toUpperCase()),
      explanation: `Helps ${helpsMatch[2]}.`,
    };
  }
  if (/^quiet spaces? reduce/i.test(text)) {
    return { headline: 'Quiet spaces', explanation: `${text.replace(/^quiet spaces?/i, 'These spaces')}.` };
  }
  if (/advance warning|warning before/i.test(text)) {
    return { headline: 'Advance warning', explanation: `${text}.` };
  }
  if (/^during distress,/i.test(text)) {
    return { headline: 'Support during distress', explanation: `${text.replace(/^during distress,?\s*/i, '')}.` };
  }
  if (/\b(?:choose|choice)\b/i.test(text)) {
    return { headline: 'Offer meaningful choices', explanation: `${text}.` };
  }
  return { headline: text, explanation: `${text}.` };
};

const deduplicateFacts = (facts = []) => {
  const output = [];
  const indexByValue = new Map();
  facts.forEach((fact) => {
    const key = valueKey(getFactValue(fact));
    if (!key) return;
    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, output.length);
      output.push(fact);
    } else if (output[existingIndex].synthetic && !fact.synthetic) {
      output[existingIndex] = fact;
    }
  });
  return output;
};

const getGroupOrder = (lens) => {
  if (lens === CARE_LENS.HOSPICE) {
    return ['comfort_calm', 'difficult_moments', 'familiar_routines_surroundings', 'helpful_approaches', 'participation_success'];
  }
  if (lens === CARE_LENS.DEMENTIA) {
    return ['familiar_routines_surroundings', 'helpful_approaches', 'comfort_calm', 'difficult_moments', 'participation_success'];
  }
  if (lens === CARE_LENS.AUTISM) {
    return ['helpful_approaches', 'comfort_calm', 'familiar_routines_surroundings', 'difficult_moments', 'participation_success'];
  }
  return DEFAULT_GROUP_ORDER;
};

export const buildWhatHelpsDomain = ({
  child = {},
  knowledgeFacts = [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const normalizedFacts = normalizeKnowledgeFacts(knowledgeFacts);
  const resolvedProfile = resolveSupportProfileFacts({
    knowledgeFacts: normalizedFacts,
    legacySupportProfile: child.supportProfile || child.supportGoals || {},
  });
  const fieldResolvedFacts = ELIGIBLE_PROFILE_FIELDS.flatMap(
    (field) => resolvedProfile.factsByField[field] || []
  ).filter((fact) => !isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS));
  const fieldResolvedIds = new Set(fieldResolvedFacts.map((fact) => fact.id));
  const facts = deduplicateFacts([
    ...fieldResolvedFacts,
    ...resolveKnowledgeFactsForProfileChapter(
      normalizedFacts,
      PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS
    ).filter((fact) => !fieldResolvedIds.has(fact.id)),
  ]);
  const lens = getCareLensKey({
    selectedConditions: child.conditions || [],
    careContext: careContext || child.careContext || {},
  });
  const groupOrder = getGroupOrder(lens);
  const rankedFacts = facts.map((fact, index) => ({
    fact,
    groupId: classifyFact(getFactValue(fact)),
    index,
  })).sort((left, right) => (
    Number(Boolean(right.fact.profileVisibilityOverride === 'pin'))
      - Number(Boolean(left.fact.profileVisibilityOverride === 'pin'))
    || groupOrder.indexOf(left.groupId) - groupOrder.indexOf(right.groupId)
    || left.index - right.index
  ));
  const handbookGroups = groupOrder.map((groupId) => ({
    id: groupId,
    title: GROUPS[groupId],
    entries: rankedFacts.filter((item) => item.groupId === groupId).map(({ fact }) => {
      const sourcePresentation = fact.synthetic ? undefined : buildProfileSourcePresentation(fact);
      return {
        id: `entry-${fact.id}`,
        knowledgeFactId: fact.synthetic ? undefined : fact.id,
        ...buildHandbookCopy(getFactValue(fact), {
          isDirectCaregiverEntry: sourcePresentation?.sourceKind === 'caregiver_direct',
        }),
        storyText: getFactValue(fact),
        responseGuidance: fact.responseGuidance || undefined,
        provenance: fact.synthetic ? undefined : buildProvenance([fact]),
        sourcePresentation,
        sourceAction: resolveFactSourceAction(fact),
      };
    }),
  })).filter((group) => group.entries.length);

  return {
    id: 'what-helps',
    title: PROFILE_CHAPTER_TITLES.WHAT_HELPS,
    caregiverQuestion: 'What helps this person feel comfortable, calm, or successful?',
    briefing: {
      items: rankedFacts.slice(0, 3).map(({ fact }) => ({
        id: `briefing-${fact.id}`,
        text: getFactValue(fact).replace(/[.]+$/, ''),
        explainedByEntryIds: [`entry-${fact.id}`],
      })),
      provenance: buildProvenance(facts.filter((fact) => !fact.synthetic)),
    },
    handbook: { groups: handbookGroups },
    emptyState: {
      title: 'What helps this person feel comfortable, calm, or successful?',
      description: 'Add the routines, approaches, people, objects, or surroundings that usually help.',
    },
    metadata: {
      domainId: WHAT_HELPS_DOMAIN_ID,
      careFocus: lens,
      ageGroup: ageGroup || careContext?.ageGroup || 'unknown',
      contractVersion: '1',
    },
  };
};
